/**
 * Protected Component - Conditional rendering based on roles and permissions
 */

import React from 'react';
import { useRole } from '../../contexts/RoleContext';

export const ProtectedComponent = ({ 
  children, 
  role, 
  roles, 
  permission, 
  permissions, 
  requireAll = false,
  fallback = null,
  showFallback = false 
}) => {
  const {
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAuthenticated
  } = useRole();

  // Check authentication first
  if (!isAuthenticated) {
    return showFallback ? fallback : null;
  }

  let hasAccess = true;

  // Check single role
  if (role && !hasRole(role)) {
    hasAccess = false;
  }

  // Check multiple roles
  if (roles && !hasAnyRole(roles)) {
    hasAccess = false;
  }

  // Check single permission
  if (permission && !hasPermission(permission)) {
    hasAccess = false;
  }

  // Check multiple permissions
  if (permissions) {
    if (requireAll) {
      if (!hasAllPermissions(permissions)) {
        hasAccess = false;
      }
    } else {
      if (!hasAnyPermission(permissions)) {
        hasAccess = false;
      }
    }
  }

  if (!hasAccess) {
    return showFallback ? fallback : null;
  }

  return <>{children}</>;
};

// Convenience components for specific roles
export const PlatformAdminOnly = ({ children, fallback = null }) => (
  <ProtectedComponent role="platform_admin" fallback={fallback}>
    {children}
  </ProtectedComponent>
);

export const ClubAdminOnly = ({ children, fallback = null }) => (
  <ProtectedComponent role="club_admin" fallback={fallback}>
    {children}
  </ProtectedComponent>
);

export const CoachOnly = ({ children, fallback = null }) => (
  <ProtectedComponent role="coach" fallback={fallback}>
    {children}
  </ProtectedComponent>
);

export const ParentOnly = ({ children, fallback = null }) => (
  <ProtectedComponent role="parent" fallback={fallback}>
    {children}
  </ProtectedComponent>
);

export const PlayerOnly = ({ children, fallback = null }) => (
  <ProtectedComponent role="player" fallback={fallback}>
    {children}
  </ProtectedComponent>
);

// Higher-order component for protecting entire components
export const withRoleProtection = (Component, roleConfig) => {
  return function ProtectedWrapper(props) {
    return (
      <ProtectedComponent {...roleConfig}>
        <Component {...props} />
      </ProtectedComponent>
    );
  };
};